import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigationType } from 'react-router';

import { useDispatch } from '@tmapy/redux';
import { useMessage } from '@tmapy/intl';

import { WaitingAlert, DangerAlert, CtaBtn, Table } from '@tmapy/style-guide';

import { TableWithAsideLayout } from '../../app/components/TableWithAsideLayout';
import { FilterBtn } from '../../app/components/FilterBtn';
import { MainContent } from '../../app/components/MainContent';
import { PageTitle } from '../../app/components/PageTitle';
import { LoadNextItemsBar } from '../../app/components/LoadNextItemsBar';
import { PublishsetListItem } from './PublishsetListItem';
import { publishsetMessages as msg, publishsetBtnMessages } from './publishsetMessages';

import { useHistoryAction, useSelector } from '../../hooks';

import { QUERY_STATE } from '../../constants';
import { PublishsetFilter } from './PublishsetFilter';
import { usePublishsetsListResource } from '../../openapi/operations/publishsetsList';

export const PublishsetList: React.FC = () => {
  const dispatch = useDispatch();
  const navigationType = useNavigationType();
  const formatMessage = useMessage();
  const [isFilterOpen, setFilterOpen] = useState(false);
  const handleNewPublishsetBtnClick = useHistoryAction('vydejNewPublishset');
  const filters = useSelector((state) => state.filter.publishsetList);
  const hideTags = useSelector((state) => state.app.config.vydej?.hideTags);

  const query: Record<string, string | number> = {};
  for (const [key, val] of Object.entries(filters)) {
    if (typeof val !== 'undefined' && val !== '') {
      (query as any)[key] = val;
    }
  }

  const { queryState, data, fetchMore } = usePublishsetsListResource(
    query,
    navigationType !== 'PUSH',
  );

  const publishsetList = data?.results ?? [];
  const publishsetCount = data?.count;

  function handleNextPublishsetsBtnClick() {
    if (data?.next) {
      fetchMore(data.next);
    }
  }

  function handleFilterBtnClick() {
    setFilterOpen(!isFilterOpen);
  }

  const isFiltered = Object.values(filters).some((filter) => filter);

  return (
    <TableWithAsideLayout
      asideLabel={formatMessage(publishsetBtnMessages.filter)}
      isAsideOpen={isFilterOpen}
      onAsideClose={handleFilterBtnClick}
    >
      <MainContent width='fit' scrollRestorationPrefix='publishsetlist'>
        <article className='sg-u-vs-2'>
          <header className='sg-a-d-f sg-a-jc-sb sg-a-ai-b sg-a-cg-2'>
            <PageTitle>
              <FormattedMessage {...msg.publishsetListPageTitle} />
            </PageTitle>
            <span className='sg-a-f-a' />
            <CtaBtn onClick={handleNewPublishsetBtnClick} size="sml">
              <FormattedMessage {...msg.publishsetListBtnNew} />
            </CtaBtn>
            <FilterBtn isFiltered={isFiltered} onClick={handleFilterBtnClick} />
          </header>
          {queryState === QUERY_STATE.WAITING && (
            <WaitingAlert>
              <FormattedMessage {...msg.publishsetListStateWaiting} />
            </WaitingAlert>
          )}
          {queryState === QUERY_STATE.FAIL && (
            <DangerAlert>
              <FormattedMessage {...msg.publishsetListStateFail} />
            </DangerAlert>
          )}
          {publishsetList.length > 0 && (
            <div className='sg-u-box sg-a-px-2 sg-a-pb-2 sg-u-vs-2'>
              <div className='sg-a-mx--2'>
                <Table isClickable>
                  <thead>
                    <tr>
                      <th>
                        <FormattedMessage {...msg.name} />
                      </th>
                      <th className='sg-a-ta-c'>
                        <FormattedMessage {...msg.state} />
                      </th>
                      <th className='sg-a-ta-c'>
                        <FormattedMessage {...msg.type} />
                      </th>
                      <th className='sg-a-ta-c'>
                        <FormattedMessage {...msg.priceListItemColumn} />
                      </th>
                      {!hideTags && (
                        <th>
                          <FormattedMessage {...msg.tags} />
                        </th>
                      )}
                      <th>
                        <FormattedMessage {...msg.created} />
                      </th>
                      <th>
                        <FormattedMessage {...msg.published} />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {publishsetList.map((publishsetItem) => (
                      <PublishsetListItem key={publishsetItem.id} {...publishsetItem} />
                    ))}
                  </tbody>
                </Table>
              </div>
              <LoadNextItemsBar
                count={publishsetList.length}
                total={publishsetCount!}
                queryState={queryState}
                onNextBtnClick={handleNextPublishsetsBtnClick}
              />
            </div>
          )}
        </article>
      </MainContent>
      <PublishsetFilter filters={filters} dispatch={dispatch} />
    </TableWithAsideLayout>
  );
};
